import { useEffect, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/shared/api/queryKeys'
import { useAuth } from '@/shared/auth/useAuth'
import { fetchSlipCompany, type CashTransfer } from './cashdeskApi'
import { TransferSlipPrintPortal } from './TransferSlip'
import { transferSlipImagesSettled } from './transferSlipPrint'
import type { CashTransferType } from './transferTypeApi'

export interface TransferSlipPrinter {
  print: (transfer: CashTransfer) => void
  portal: ReactNode
}

export function useTransferSlipPrinter(types: readonly CashTransferType[] | undefined): TransferSlipPrinter {
  const { user } = useAuth()
  const [transfer, setTransfer] = useState<CashTransfer | null>(null)
  const slipCompany = useQuery({
    queryKey: queryKeys.cashdesk.slipCompany,
    queryFn: ({ signal }) => fetchSlipCompany(signal),
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    if (transfer === null) return
    let cancelled = false
    const run = async () => {
      await new Promise((resolve) => requestAnimationFrame(resolve))
      await transferSlipImagesSettled()
      if (cancelled) return
      window.print()
      setTransfer(null)
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [transfer])

  const company = {
    name: slipCompany.data?.name ?? user?.tenant.name ?? null,
    logoUrl: slipCompany.data?.logoUrl ?? null,
  }

  return {
    print: setTransfer,
    portal: transfer ? <TransferSlipPrintPortal transfer={transfer} types={types} company={company} /> : null,
  }
}
